import { Encodings } from "@pdf-lib/standard-fonts";
import { PDFDocument, StandardFonts, type PDFFont } from "pdf-lib";

const winAnsiEncoding = Encodings.WinAnsi;
const PDF_CONTENT_TYPE = "application/pdf";
const US_LETTER_PAGE_SIZE: [number, number] = [612, 792];
const PLAIN_TEXT_PDF_MARGIN = 50;
const PLAIN_TEXT_PDF_FONT_SIZE = 10;
const PLAIN_TEXT_PDF_LINE_HEIGHT = 14;

export function isPdfContentType(contentType?: string | null): boolean {
  return contentType?.split(";")[0].trim().toLowerCase() === PDF_CONTENT_TYPE;
}

/**
 * pdf-lib standard fonts use WinAnsi; many Unicode characters (e.g. U+202F, CJK) cannot be encoded.
 * Use before page.drawText when embedding Helvetica et al. without a custom font.
 */
export function sanitizeTextForStandardPdfFont(text: string): string {
  const normalized = text
    .normalize("NFKC")
    .replace(/\p{Zs}/gu, " ")
    .replace(/\p{Cf}/gu, "");

  let result = "";

  for (const char of normalized) {
    const codePoint = char.codePointAt(0)!;

    if (codePoint === 0x09 || codePoint === 0x0a || codePoint === 0x0d) {
      result += char;
      continue;
    }

    if (winAnsiEncoding.canEncodeUnicodeCodePoint(codePoint)) {
      result += char;
    } else {
      result += "?";
    }
  }

  return result;
}

export async function renderPlainTextAsPdf(text: string): Promise<Buffer> {
  const pdfDocument = await PDFDocument.create();
  const font = await pdfDocument.embedFont(StandardFonts.Helvetica);
  const maxWidth = US_LETTER_PAGE_SIZE[0] - PLAIN_TEXT_PDF_MARGIN * 2;
  const sourceLines = sanitizeTextForStandardPdfFont(text).replace(/\r\n?/g, "\n").replace(/\t/g, "    ").split("\n");
  let page = pdfDocument.addPage(US_LETTER_PAGE_SIZE);
  let y = page.getHeight() - PLAIN_TEXT_PDF_MARGIN - PLAIN_TEXT_PDF_FONT_SIZE;

  for (const sourceLine of sourceLines) {
    const lines = wrapPdfTextLine(sourceLine, maxWidth, PLAIN_TEXT_PDF_FONT_SIZE, font);

    for (const line of lines) {
      if (y < PLAIN_TEXT_PDF_MARGIN) {
        page = pdfDocument.addPage(US_LETTER_PAGE_SIZE);
        y = page.getHeight() - PLAIN_TEXT_PDF_MARGIN - PLAIN_TEXT_PDF_FONT_SIZE;
      }

      if (line) {
        page.drawText(line, {
          x: PLAIN_TEXT_PDF_MARGIN,
          y,
          size: PLAIN_TEXT_PDF_FONT_SIZE,
          font,
        });
      }

      y -= PLAIN_TEXT_PDF_LINE_HEIGHT;
    }
  }

  return Buffer.from(await pdfDocument.save());
}

function wrapPdfTextLine(line: string, maxWidth: number, fontSize: number, font: PDFFont): string[] {
  if (!line) return [""];

  const lines: string[] = [];
  let remaining = line;

  while (font.widthOfTextAtSize(remaining, fontSize) > maxWidth) {
    let low = 1;
    let high = remaining.length;
    let fittingLength = 1;

    while (low <= high) {
      const midpoint = Math.floor((low + high) / 2);

      if (font.widthOfTextAtSize(remaining.slice(0, midpoint), fontSize) <= maxWidth) {
        fittingLength = midpoint;
        low = midpoint + 1;
      } else {
        high = midpoint - 1;
      }
    }

    const whitespaceIndex = remaining.lastIndexOf(" ", fittingLength);
    const splitIndex = whitespaceIndex > 0 ? whitespaceIndex : fittingLength;

    lines.push(remaining.slice(0, splitIndex).trimEnd());
    remaining = remaining.slice(splitIndex).trimStart();
  }

  lines.push(remaining);

  return lines;
}

/**
 * Splits a PDF buffer into chunks where each chunk stays within the specified max size.
 * Throws if a single page exceeds the max chunk size.
 */
export async function splitPdfIntoChunks(fileBuffer: Buffer, maxChunkSize: number): Promise<Buffer[]> {
  const pdfDoc = await PDFDocument.load(fileBuffer);
  const totalPages = pdfDoc.getPageCount();
  const chunks: Buffer[] = [];

  let currentChunkStart = 0;

  while (currentChunkStart < totalPages) {
    const remainingPages = totalPages - currentChunkStart;

    let low = 1;
    let high = remainingPages;
    let bestBytes: Uint8Array | null = null;
    let bestPageCount = 0;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const candidateBytes = await buildChunkPdf(pdfDoc, currentChunkStart, mid);

      if (candidateBytes.length <= maxChunkSize) {
        bestBytes = candidateBytes;
        bestPageCount = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    if (!bestBytes) {
      const singlePageBytes = await buildChunkPdf(pdfDoc, currentChunkStart, 1);

      throw new Error(
        `Single PDF page ${currentChunkStart + 1} exceeds max chunk size (${singlePageBytes.length} bytes > ${maxChunkSize} bytes)`
      );
    }

    chunks.push(Buffer.from(bestBytes));
    currentChunkStart += bestPageCount;
  }

  return chunks;
}

async function buildChunkPdf(sourcePdf: PDFDocument, startPage: number, pageCount: number): Promise<Uint8Array> {
  const chunkDoc = await PDFDocument.create();
  const pageIndices = Array.from({ length: pageCount }, (_, i) => startPage + i);
  const pages = await chunkDoc.copyPages(sourcePdf, pageIndices);

  for (const page of pages) {
    chunkDoc.addPage(page);
  }

  return chunkDoc.save();
}
